import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { stockActions, Product } from "../store/slices/stockSlice";
import customAxios from "../utils/customAxios";
import { Toast } from "react-native-toast-notifications";
import i18n from "../locales/i18n";

export const useStock = () => {
  const dispatch = useDispatch();
  const { products, isLoading, lastFetch, error } = useSelector(
    (state: RootState) => state.stock
  );

  // Check if we need to refresh data (cache for 5 minutes)
  const shouldRefresh = useMemo(() => {
    if (!lastFetch) return true;
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() - lastFetch > fiveMinutes;
  }, [lastFetch]);

  // Fetch products from API
  const fetchProducts = useCallback(
    async (forceRefresh = false) => {
      try {
        if (!forceRefresh && !shouldRefresh && products.length > 0) {
          return products;
        }

        dispatch(stockActions.setLoading(true));
        dispatch(stockActions.clearError());

        const res = await customAxios.get("/stock/", {
          withCredentials: true,
          timeout: 10000, // 10 second timeout
        });

        // Handle different possible response structures with safe access
        let fetchedProducts = [];
        try {
          if (res?.data) {
            if (Array.isArray(res.data)) {
              fetchedProducts = res.data;
            } else if (res.data?.products && Array.isArray(res.data.products)) {
              fetchedProducts = res.data.products;
            } else if (res.data?.data && Array.isArray(res.data.data)) {
              fetchedProducts = res.data.data;
            } else {
              fetchedProducts = [];
            }
          }
        } catch (dataError) {
          fetchedProducts = [];
        }

        dispatch(stockActions.setProducts(fetchedProducts));
        return fetchedProducts;
      } catch (error: any) {
        // Handle "no products found" gracefully (404 with specific message)
        if (
          error.response?.status === 404 &&
          (error.response?.data?.error === "No products found" ||
            error.response?.data?.message === "No products found")
        ) {
          // This is not an error - just no products yet
          dispatch(stockActions.setProducts([]));
          return [];
        }

        // Handle real errors
        const errorMessage =
          error.code === "ECONNABORTED"
            ? "Request timeout - please check your connection"
            : i18n.t("loadProductsError");

        dispatch(stockActions.setError(errorMessage));
        Toast.show(errorMessage, {
          type: "danger",
        });
        return [];
      } finally {
        dispatch(stockActions.setLoading(false));
      }
    },
    [dispatch, shouldRefresh, products.length] // Changed dependency to avoid infinite loops
  );

  // Search products locally
  const searchProducts = useCallback(
    (searchTerm: string): Product[] => {
      if (!searchTerm || searchTerm.trim() === "") {
        return [];
      }

      const term = searchTerm.toLowerCase().trim();

      try {
        const filtered = products.filter((product, index) => {
          try {
            // Safe access to product properties
            if (!product) return false;

            // Always check name with safe access
            const nameMatch =
              product.name &&
              typeof product.name === "string" &&
              product.name.toLowerCase().includes(term);

            // Safe barcode checking
            let barcodeMatch = false;
            if (product.barcode) {
              if (typeof product.barcode === "string") {
                barcodeMatch = product.barcode.toLowerCase().includes(term);
              } else if (typeof product.barcode === "number") {
                barcodeMatch = product.barcode.toString().includes(term);
              }
            }

            return nameMatch || barcodeMatch;
          } catch (productError) {
            return false;
          }
        });

        return filtered;
      } catch (error) {
        return [];
      }
    },
    [products]
  );

  // Add new product
  const addProduct = useCallback(
    (product: Product) => {
      dispatch(stockActions.addProduct(product));
    },
    [dispatch]
  );

  // Update existing product
  const updateProduct = useCallback(
    (product: Product) => {
      dispatch(stockActions.updateProduct(product));
    },
    [dispatch]
  );

  // Remove product from store
  const removeProduct = useCallback(
    (productId: number) => {
      dispatch(stockActions.removeProduct(productId));
    },
    [dispatch]
  );

  // Delete product from API and store
  const deleteProduct = useCallback(
    async (productId: number): Promise<boolean> => {
      try {
        await customAxios.delete(`/stock/${productId}`, {
          withCredentials: true,
        });

        // Remove from local store
        dispatch(stockActions.removeProduct(productId));

        Toast.show(i18n.t("productDeleted"), {
          type: "success",
        });
        return true;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          i18n.t("deleteFailed");
        Toast.show(errorMessage, {
          type: "danger",
        });
        return false;
      }
    },
    [dispatch]
  );

  // Force reset loading state
  const forceResetLoading = useCallback(() => {
    dispatch(stockActions.forceResetLoading());
  }, [dispatch]);

  // Filter products by criteria
  const filterProducts = useCallback(
    (products: Product[], filter: string): Product[] => {
      switch (filter) {
        case "low_stock":
          return products.filter((item) => item.stock <= 10);
        case "high_value":
          return products.filter((item) => parseFloat(item.price_sell) >= 100);
        default:
          return products;
      }
    },
    []
  );

  // Sort products
  const sortProducts = useCallback(
    (products: Product[], sortBy: string): Product[] => {
      const sorted = [...products];

      switch (sortBy) {
        case "name_asc":
          return sorted.sort((a, b) => a.name.localeCompare(b.name));
        case "name_desc":
          return sorted.sort((a, b) => b.name.localeCompare(a.name));
        case "price_asc":
          return sorted.sort(
            (a, b) => parseFloat(a.price_sell) - parseFloat(b.price_sell)
          );
        case "price_desc":
          return sorted.sort(
            (a, b) => parseFloat(b.price_sell) - parseFloat(a.price_sell)
          );
        case "stock_asc":
          return sorted.sort((a, b) => a.stock - b.stock);
        case "stock_desc":
          return sorted.sort((a, b) => b.stock - a.stock);
        default:
          return sorted;
      }
    },
    []
  );

  return {
    products,
    isLoading,
    error,
    fetchProducts,
    searchProducts,
    addProduct,
    updateProduct,
    removeProduct,
    deleteProduct,
    filterProducts,
    sortProducts,
    forceResetLoading,
  };
};
